import { useEffect, useRef, useState } from "react";

import {
  downloadAuthenticatedFile,
  makeGetApiCall,
  makePostApiCall,
} from "../../shared/makeApiCall";
import { BannerNotificationType } from "../../components/SimpleNotification";
import ModelErrorComponent from "../../components/modals/ModelErrorComponent";
import ProgressModal from "../../components/modals/ProgressModal";

export function QaReportsViewModel(logout) {
  const defaultFilters = {
    class: { value: -1, label: "All classes" },
    category: { value: -1, label: "All categories" },
    product: { value: -1, label: "All products" },
    supplier: { value: -1, label: "All suppliers" },
    startDate: null,
    endDate: null,
  };
  const qaReportOptions = [
    { value: 5, label: "Deleted Cartons" },
    { value: 6, label: "Inventory Valuation" },
    { value: 8, label: "Flow Reports" },
    { value: 7, label: "Open WIPs Report" },
    { value: 9, label: "Production Detail" },
    { value: 10, label: "Production Summary" },
    { value: 1, label: "Receiving Details" },
    { value: 2, label: "Receiving Summary" },
    { value: 3, label: "Reorder Limit Items" },
    { value: 4, label: "Sales report by PLU" },
  ];
  const [filters, setFilters] = useState(defaultFilters);
  const [emptyModal, setEmptyModelOpen] = useState({
    open: false,
    childComponent: null,
  });
  const [loading, setLoading] = useState(false);
  const [reportType, setReportType] = useState({
    value: -1,
    label: "Select report",
  });
  const [showSimpleNotification, setShowSimpleNotification] = useState({
    show: false,
    message: null,
    title: null,
    type: BannerNotificationType.Success,
  });
  const [categories, setCategories] = useState([]);
  const [classes, setClasses] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [recentReports, setRecentReports] = useState([]);
  const [recentReportsLoading, setRecentReportsLoading] = useState(true);
  const [recentReportsError, setRecentReportsError] = useState(null);
  const pollingIds = useRef(new Set());
  const mounted = useRef(true);

  useEffect(() => {
    const activePollingIds = pollingIds.current;
    mounted.current = true;

    return () => {
      mounted.current = false;
      activePollingIds.clear();
    };
  }, []);

  const showProgressModal = (message) => {
    setEmptyModelOpen({
      open: true,
      childComponent: <ProgressModal message={message} />,
    });
  };

  const showErrorModal = (title, subtitle) => {
    setEmptyModelOpen({
      childComponent: (
        <ModelErrorComponent
          setOpen={setEmptyModelOpen}
          subtitle={subtitle}
          title={title}
        />
      ),
      open: true,
    });
  };

  const getCategoryTypes = async function () {
    const callResult = await makeGetApiCall("categoriesForDropdown");
    if (callResult.success) {
      setCategories([
        { value: -1, label: "All categories" },
        ...callResult.data,
      ]);
    } else {
      if (callResult.errorStatus === 401) logout();
      showErrorModal("Could not fetch category types");
    }
  };

  const getClassTypes = async function () {
    const callResult = await makeGetApiCall("classesForDropdown");
    if (callResult.success) {
      setClasses([{ value: -1, label: "All classes" }, ...callResult.data]);
    } else if (callResult.errorStatus === 401) {
      logout();
    }
  };

  const getCustomersForDropdown = async function () {
    const callResult = await makeGetApiCall("customersForDropdown");
    if (callResult.success) {
      setCustomers([{ value: -1, label: "All customers" }, ...callResult.data]);
    } else if (callResult.errorStatus === 401) {
      logout();
    }
  };

  const getProductOptionsForDropdown = async function () {
    const callResult = await makeGetApiCall("productsForDropdown");
    if (callResult.success) {
      setProducts([{ value: -1, label: "All products" }, ...callResult.data]);
    } else if (callResult.errorStatus === 401) {
      logout();
    }
  };

  const getSupplierOptionsForDropdown = async function () {
    const callResult = await makeGetApiCall("suppliersForDropdown");
    if (callResult.success) {
      setSuppliers([{ value: -1, label: "All suppliers" }, ...callResult.data]);
    } else {
      if (callResult.errorStatus === 401) logout();
      showErrorModal("Could not fetch suppliers");
    }
  };

  const upsertReport = (report) => {
    if (!mounted.current) return;

    setRecentReports((current) => {
      const exists = current.some((item) => item.id === report.id);
      if (!exists) return [report, ...current];
      return current.map((item) => (item.id === report.id ? report : item));
    });
  };

  const downloadReport = async (report) => {
    if (!report.download_url) return;

    const result = await downloadAuthenticatedFile(
      report.download_url,
      report.output_name || `${report.report_name || "report"}.pdf`
    );
    if (!result.success) {
      if (result.errorStatus === 401) logout();
      showErrorModal(result.data?.message || "Could not download the report");
    }
  };

  const pollReport = async (initialReport, downloadWhenReady = false) => {
    if (pollingIds.current.has(initialReport.id) && !downloadWhenReady) return;

    pollingIds.current.add(initialReport.id);
    let report = initialReport;

    try {
      while (
        mounted.current &&
        ["queued", "processing"].includes(report.status)
      ) {
        await new Promise((resolve) => setTimeout(resolve, 2000));
        if (!mounted.current) return;

        const result = await makeGetApiCall(report.status_url);
        if (!result.success) {
          if (result.errorStatus === 401) logout();
          if (downloadWhenReady) {
            setEmptyModelOpen({ open: false, childComponent: null });
            showErrorModal(
              result.data?.message || "Could not check report status"
            );
          }
          return;
        }

        report = result.data;
        upsertReport(report);
      }

      if (!mounted.current || !downloadWhenReady) return;

      setEmptyModelOpen({ open: false, childComponent: null });
      if (report.status === "completed") {
        await downloadReport(report);
      } else if (report.status === "failed") {
        showErrorModal("Report generation failed", report.error_message);
      }
    } finally {
      pollingIds.current.delete(initialReport.id);
    }
  };

  const getRecentReports = async () => {
    setRecentReportsLoading(true);
    setRecentReportsError(null);

    const result = await makeGetApiCall("qa-report-requests?per_page=50");
    if (result.success) {
      const reports = result.data.data || [];
      if (mounted.current) setRecentReports(reports);
      reports
        .filter((report) => ["queued", "processing"].includes(report.status))
        .forEach((report) => pollReport(report));
    } else if (result.errorStatus === 401) {
      logout();
    } else if (result.errorStatus === 404) {
      if (mounted.current) setRecentReports([]);
    } else if (mounted.current) {
      setRecentReportsError(
        "Could not load recent reports. Refresh the page to try again."
      );
    }

    if (mounted.current) setRecentReportsLoading(false);
  };

  const printSelectedReport = async function (format = "pdf") {
    const outputFormat = format === "xlsx" ? "xlsx" : "pdf";
    if (reportType.value === -1) {
      showErrorModal("Please select a report type");
      return;
    }
    if (
      reportType.value !== 7 &&
      (!filters.startDate || (reportType.value !== 6 && !filters.endDate))
    ) {
      showErrorModal("Please select a date range");
      return;
    }
    if (
      [4, 8, 9].includes(reportType.value) &&
      (!filters.product || filters.product.value === -1)
    ) {
      showErrorModal("Please select a product");
      return;
    }
    if (outputFormat === "xlsx" && reportType.value !== 6) {
      showErrorModal("Excel is available for the inventory valuation report.");
      return;
    }
    showProgressModal(
      outputFormat === "xlsx"
        ? "Submitting Excel report..."
        : "Submitting report..."
    );

    const reportUrl = {
      1: "generateGeneralReceivingDetailsReport",
      2: "generateGeneralReceivingSummaryReport",
      3: "reorderLimitReport",
      4: "salesReportByPlu",
      5: "deletedCartonsReport",
      6: "inventoryValuationReport",
      7: "openWipsReport",
      8: "inventoryFlow",
      9: "productionDetailsReport",
      10: "productionSummaryReport",
    }[reportType.value];
    const reportTypeKey = {
      1: "receiving_details",
      2: "receiving_summary",
      3: "reorder_limit",
      4: "sales_by_plu",
      5: "deleted_cartons",
      6: "inventory_valuation",
      7: "open_wips",
      8: "inventory_flow",
      9: "production_details",
      10: "production_summary",
    }[reportType.value];
    const parameters = {
      ...filters,
      endDate: reportType.value === 6 ? filters.startDate : filters.endDate,
    };
    if (outputFormat === "xlsx") {
      parameters.format = "xlsx";
    }

    setLoading(true);
    let callResult = await makePostApiCall("qa-report-requests", {
      report_type: reportTypeKey,
      parameters,
    });

    // Keep frontend-first deployments compatible with the synchronous PDF API.
    if (
      outputFormat !== "xlsx" &&
      !callResult.success &&
      callResult.errorStatus === 404
    ) {
      showProgressModal("Generating report...");
      callResult = await makePostApiCall(reportUrl, { filter: parameters });
    }

    if (callResult.success) {
      if (callResult.data.status_url) {
        upsertReport(callResult.data);
        await pollReport(callResult.data, true);
      } else {
        window.open(callResult.data.location, "_blank");
        setEmptyModelOpen({ open: false, childComponent: null });
      }
    } else {
      if (callResult.errorStatus === 401) logout();
      setEmptyModelOpen({ open: false, childComponent: null });
      showErrorModal(callResult.data?.message || "Could not submit the report");
    }

    setLoading(false);
    return callResult;
  };

  const retryReport = async (report) => {
    const result = await makePostApiCall(
      `qa-report-requests/${report.id}/retry`,
      {}
    );
    if (!result.success) {
      if (result.errorStatus === 401) logout();
      showErrorModal(
        result.data?.message || "Could not generate the report again"
      );
      return;
    }

    upsertReport(result.data);
    void pollReport(result.data, true);
  };

  function resetFilters() {
    setFilters(defaultFilters);
  }

  return {
    emptyModal,
    loading,
    setEmptyModelOpen,
    setShowSimpleNotification,
    showSimpleNotification,
    filters,
    printSelectedReport,
    qaReportOptions,
    setFilters,
    reportType,
    setReportType,
    resetFilters,
    categories,
    products,
    suppliers,
    getCategoryTypes,
    getCustomersForDropdown,
    getProductOptionsForDropdown,
    getSupplierOptionsForDropdown,
    classes,
    getClassTypes,
    recentReports,
    recentReportsLoading,
    recentReportsError,
    getRecentReports,
    downloadReport,
    retryReport,
  };
}
